import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaSearch, FaRegClock, FaChartLine, FaRobot, FaBrain, FaMicrochip, FaShoppingCart, FaMobileAlt, FaEye, FaLeaf, FaMedkit } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import '../styles/CaseStudies.css';
import ModernCTA from '../components/ModernCTA.js';

const CaseStudies = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const caseStudies = [
    {
      id: 1,
      title: "Enterprise E-Commerce Platform",
      client: "Global Retail Brand",
      category: "ecommerce",
      duration: "8 months",
      results: "Increased online sales by 120%",
      image: "https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["React", "Node.js", "MongoDB", "Payment Gateways"]
    },
    {
      id: 2,
      title: "AI-Powered Surveillance System",
      client: "Smart City Initiative",
      category: "surveillance",
      duration: "6 months",
      results: "Reduced security incidents by 75%",
      image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["Computer Vision", "Edge Computing", "TensorFlow"]
    },
    {
      id: 3,
      title: "Precision Farming Solution",
      client: "Agricultural Cooperative",
      category: "agritech",
      duration: "5 months",
      results: "Increased crop yield by 35%",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",      tech: ["Drone Imagery", "IoT Sensors", "ML Algorithms"]
    },
    {
      id: 4,
      title: "Healthcare Diagnostics Platform",
      client: "Regional Hospital Network",
      category: "healthtech",
      duration: "7 months",
      results: "Improved diagnosis accuracy by 45%",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["AI Diagnostics", "DICOM Processing", "HIPAA Compliance"]
    },
    {
      id: 5,
      title: "Enterprise Resource Planning System",
      client: "Manufacturing Conglomerate",
      category: "enterprise",
      duration: "10 months",
      results: "Streamlined operations across 12 departments",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["Java", "Spring Boot", "Microservices"]
    },
    {
      id: 6,
      title: "Mobile Banking Application",
      client: "Financial Services Provider",
      category: "mobile",
      duration: "4 months",
      results: "User adoption increased by 90%",
      image: "https://images.unsplash.com/photo-1601597111158-2fceff292cdc?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["React Native", "Biometric Auth", "Banking APIs"]
    },
    {
      id: 7,
      title: "SEO Optimization Suite",
      client: "Digital Marketing Agency",
      category: "seo",
      duration: "3 months",
      results: "Organic traffic growth of 200%",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["Keyword Analysis", "Content Strategy", "Analytics"]
    },
    {
      id: 8,
      title: "Web Application Modernization",
      client: "Legacy Software Provider",
      category: "webdev",
      duration: "6 months",
      results: "Improved performance by 300%",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["Vue.js", "GraphQL", "AWS Serverless"]
    },
    {
      id: 9,
      title: "Website Maintenance Program",
      client: "E-Commerce Business",
      category: "maintenance",
      duration: "Ongoing",
      results: "99.9% uptime achieved",
      image: "https://images.unsplash.com/photo-1547658719-da2b51169166?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tech: ["Proactive Monitoring", "Security Patches", "Performance Tuning"]
    }
  ];

  const filters = [
    { id: 'all', label: 'All Services', icon: <FaRobot /> },
    { id: 'webdev', label: 'Web Development', icon: <FaMicrochip /> },
    { id: 'maintenance', label: 'Web Maintenance', icon: <FaChartLine /> },
    { id: 'ecommerce', label: 'E-Commerce', icon: <FaShoppingCart /> },
    { id: 'seo', label: 'Digital Growth', icon: <FaBrain /> },
    { id: 'enterprise', label: 'Enterprise Software', icon: <FaMicrochip /> },
    { id: 'mobile', label: 'Mobile Innovation', icon: <FaMobileAlt /> },
    { id: 'surveillance', label: 'AI Surveillance', icon: <FaEye /> },
    { id: 'agritech', label: 'AgriTech AI', icon: <FaLeaf /> },
    { id: 'healthtech', label: 'HealthTech AI', icon: <FaMedkit /> }
  ];

  const filteredStudies = caseStudies.filter(study => {
    const matchesFilter = activeFilter === 'all' || study.category === activeFilter;
    const matchesSearch = study.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         study.client.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="case-studies-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Animated Background Elements */}
      <div className="case-studies-bg-elements">
        <div className="bg-dot-grid"></div>
        <div className="bg-blur-circle circle-1"></div>
        <div className="bg-blur-circle circle-2"></div>
      </div>

      <div className="case-studies-container">
        {/* Hero Section */}
        <motion.section 
          className="case-studies-hero"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h1>Our <span className="gradient-text">Success Stories</span></h1>
          <p>Explore how our digital solutions and AI implementations are transforming businesses across industries</p>
        </motion.section>

        {/* Filters and Search */}
        <motion.section 
          className="filters-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input 
              type="text" 
              placeholder="Search projects..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="filter-tabs">
            <FiFilter className="filter-icon" />
            {filters.map(filter => (
              <button
                key={filter.id}
                className={`filter-tab ${activeFilter === filter.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.icon} {filter.label}
              </button>
            ))}
          </div>
        </motion.section>

        {/* Case Studies Grid */}
        {filteredStudies.length > 0 ? (
          <motion.div 
            className="case-studies-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredStudies.map(study => (
              <motion.div 
                key={study.id}
                className="case-study-card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                <div className="card-image">
                  <img src={study.image} alt={study.title} />
                  <div className="image-overlay"></div>
                  <div className="category-badge">{study.category.replace('-', ' ').toUpperCase()}</div>
                </div>
                <div className="card-content">
                  <h3>{study.title}</h3>
                  <p className="client">{study.client}</p>
                  
                  <div className="stats">
                    <div className="stat">
                      <FaRegClock />
                      <span>{study.duration}</span>
                    </div>
                    <div className="stat">
                      <FaChartLine />
                      <span>{study.results}</span>
                    </div>
                  </div>
                  
                  <div className="tech-stack">
                    <h4>Technologies Used:</h4>
                    <div className="tech-tags">
                      {study.tech.map((tech, index) => (
                        <span key={index} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div 
            className="no-results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h3>No projects found matching your criteria</h3>
            <button 
              className="reset-filters"
              onClick={() => {
                setActiveFilter('all');
                setSearchQuery('');
              }}
            >
              Reset Filters
            </button>
          </motion.div>
        )}

        {/* CTA Section */}
        <ModernCTA />
      </div>
    </motion.div>
  );
};

export default CaseStudies;